import {
  ICardPaymentBrickPayer,
  ICardPaymentFormData,
} from '@mercadopago/sdk-react/bricks/cardPayment/type'
import { useRollbar } from '@rollbar/react'
import CommonButton from 'common/components/entities/Button/button'
import { FieldSlugEnum } from 'common/enums/FieldSlugEnum'
import { BadRequest, InternalError, NetworkError } from 'common/errors'
import { centsToEuro } from 'common/utils/price-calculator'
import { buyUpsellOfferCard } from 'publisher/api/mercadoPagoApi'
import StyleWrapper from 'publisher/components/core/StyleWrapper'
import FieldErrors from 'publisher/components/FieldErrors'
import { useCalculatedPricePlans } from 'publisher/pages/offer-page/components/OfferPrice/useCalculatedPricePlans'
import { useSelectedOfferPricing } from 'publisher/pages/offer-page/hooks/useSelectedOfferPricing'
import { calculateOfferPricing } from 'publisher/pages/offer-page/utils/calculateOfferPricing'
import { AsyncMercadoPagoCardPaymentModal } from 'publisher/paymentProcessors/mercadoPago/components/cardPayment/PaymentModal'
import ThreeDsModal, {
  ThreeDsInfo,
} from 'publisher/paymentProcessors/mercadoPago/components/ThreeDsModal'
import { useOptIn, usePayment } from 'publisher/store'
import { getFieldValueBySlug } from 'publisher/store/optIn/optInSelectors'
import { PageId } from 'publisher/store/page/PageStateInterface'
import { PurchaseProcessId } from 'publisher/store/payment/PaymentStateInterface'
import { setOfferPricing } from 'publisher/store/payment/paymentActions'
import {
  getActivePricePlan,
  getOfferId,
  getProduct,
  getProductActiveVariant,
  getProductQuantity,
} from 'publisher/store/payment/paymentSelectors'
import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useCalculateButtonStyles } from 'src/publisher/shared/hooks/use-calculate-button-styles'
import { useSelectedButtonColor } from 'src/publisher/shared/hooks/use-theme-color/use-selected-button-color'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import useUpsellSubmit from '../hooks/useUpsellSubmit'
import { UpsellSubmitBodyInterface } from '../types/UpsellSubmitBodyInterface'
import { UpsellPaymentButtonPropsType } from './UpsellPaymentButton'

function MercadoPagoUpsellButton({ entity }: UpsellPaymentButtonPropsType) {
  const rollbar = useRollbar()
  const dispatch = useDispatch()
  const { t } = useTypedTranslation(['publisher'])

  const abortControllerRef = useRef<AbortController>()
  const [MercadoPagoCardPaymentModal, setMercadoPagoCardPaymentModal] =
    useState<AsyncMercadoPagoCardPaymentModal | null>(null)
  const [isModalOpened, setIsModalOpened] = useState(false)
  const [isThreeDsModalOpen, setIsThreeDsModalOpen] = useState(false)
  const [threeDsInfo, setThreeDsInfo] = useState<ThreeDsInfo>({
    creq: '',
    external_resource_url: '',
  })
  const [redirect, setRedirect] = useState<string>()

  const taxNumber = useOptIn(state =>
    getFieldValueBySlug(state, FieldSlugEnum.TaxNumber),
  )
  const customerCountry = useOptIn(state =>
    getFieldValueBySlug(state, FieldSlugEnum.Country),
  )

  const offerId = usePayment(getOfferId)
  const product = usePayment(getProduct)
  const activeProductVariant = usePayment(getProductActiveVariant)
  const productQuantity = usePayment(getProductQuantity)
  const { pricePlans } = useCalculatedPricePlans()
  const selectedPricePlan = usePayment(getActivePricePlan)

  const selectedPricing = useSelectedOfferPricing({
    selectedProduct: product,
    selectedPricePlan,
    activeProductVariant,
  })

  const paymentAmount = selectedPricing
    ? centsToEuro(selectedPricing.grossAmount)
    : null

  const { errors, setErrors, isLoading, submit } = useUpsellSubmit(entity)

  const loadMercadoPago = async () => {
    const { default: lazyComponent } = await import(
      /* webpackChunkName: "mercado-pago-card-payment" */ 'publisher/paymentProcessors/mercadoPago/components/cardPayment/PaymentModal'
    )
    setMercadoPagoCardPaymentModal(() => lazyComponent)
  }

  useEffect(() => {
    if (!offerId || (!product?.id && !pricePlans.length)) return

    const fetchPriceData = async () => {
      try {
        abortControllerRef.current = new AbortController()

        const newOfferPricings = await calculateOfferPricing({
          product,
          pricePlans,
          offerId,
          country: customerCountry,
          activeProductVariant,
          taxNumber,
          productQuantity,
          abortSignal: abortControllerRef.current.signal,
        })

        dispatch(setOfferPricing(newOfferPricings))
      } catch (error) {
        if (error instanceof BadRequest || error instanceof InternalError) {
          setErrors([t('core.error.title')])
        }
      }
    }

    if (abortControllerRef.current?.signal) {
      abortControllerRef.current.abort('new_request_sent')
    }

    fetchPriceData()
  }, [
    product?.id,
    productQuantity,
    activeProductVariant,
    customerCountry,
    offerId,
    taxNumber,
    selectedPricePlan?.id,
  ])

  useEffect(() => {
    const fetchMercadoPagoModal = async () => {
      try {
        await loadMercadoPago()
      } catch (e) {
        rollbar.error('Load mercado pago modal', e as Error)
      }
    }

    fetchMercadoPagoModal()
  }, [])

  const showCardFormModal = () => {
    setIsModalOpened(true)
  }

  const onThreeDsClose = useCallback(() => {
    setIsThreeDsModalOpen(false)
  }, [])

  const processPayment = useCallback(
    async (cardFormData: ICardPaymentFormData<ICardPaymentBrickPayer>) => {
      await submit(
        async (
          body: UpsellSubmitBodyInterface,
          pageId: PageId,
          purchaseProcessId: PurchaseProcessId,
        ) => {
          try {
            const { data } = await buyUpsellOfferCard(
              pageId,
              purchaseProcessId,
              {
                upsell_payment_form: {
                  token: cardFormData.token,
                  mercadoPagoPaymentMethod: cardFormData.payment_method_id,
                  installments: cardFormData.installments,
                  ...body,
                },
              },
            )

            if (data.threeDsInfo) {
              setThreeDsInfo(data.threeDsInfo)
              setRedirect(data.redirect)
              setIsThreeDsModalOpen(true)
              await closeModalAndUnmount()
              return
            }

            await closeModalAndUnmount()
            window.location.assign(data.redirect)
          } catch (error) {
            if (error instanceof BadRequest) {
              setErrors(error.response.data.errors.common)
            } else if (error instanceof NetworkError) {
              setErrors([t('core.errors.no_connection')])
            } else if (error instanceof InternalError) {
              setErrors([t('core.error.title')])
            } else {
              rollbar.error('mercado pago upsell failed', error as Error)
              setErrors([t('core.error.title')])
            }
          }
        },
      )
    },
    [], // dependencies makes mercado pago load again and it looks visually bad, todo: refactor useUpsellSubmit
  )

  const closeModalAndUnmount = async () => {
    setIsModalOpened(false)
    await window.cardPaymentBrickController.unmount()
  }

  const {
    calculatedBorder,
    calculatedPadding,
    calculatedFontSize,
    calculatedLineHeight,
    // calculatedTextAlign,
    // calculatedLetterSpacing,
    calculatedFontFamily,
    calculatedFontWeight,
    calculatedFontStyle,
  } = useCalculateButtonStyles({
    border: entity.border,
    mobileBorder: entity.mobileBorder,
    padding: entity.padding,
    mobilePadding: entity.mobilePadding,
    buttonType: entity.buttonType,
    fontSize: entity.textFontSize,
    mobileFontSize: entity.mobileTextFontSize,
    lineHeight: entity.lineHeight,
    mobileLineHeight: entity.mobileLineHeight,
    fontFamily: entity.textFontFamily,
    mobileFontFamily: entity.mobileTextFontFamily,
    fontWeight: entity.textFontWeight,
    mobileFontWeight: entity.mobileTextFontWeight,
    fontStyle: entity.textFontStyle,
    mobileFontStyle: entity.mobileTextFontStyle,
  })

  const {
    buttonTextColor,
    buttonBackgroundColor,
    hoveredButtonBackgroundColor,
  } = useSelectedButtonColor(entity.buttonType)

  return (
    <div>
      {isModalOpened && paymentAmount && MercadoPagoCardPaymentModal && (
        <MercadoPagoCardPaymentModal
          amount={paymentAmount}
          onClose={closeModalAndUnmount}
          processPayment={processPayment}
        />
      )}
      {isThreeDsModalOpen && (
        <ThreeDsModal
          creq={threeDsInfo.creq}
          external_resource_url={threeDsInfo.external_resource_url}
          redirect={redirect}
          onClose={onThreeDsClose}
        />
      )}
      <StyleWrapper
        flex
        flexColumn
        margin={entity.margin}
        mobileMargin={entity.mobileMargin}
        appearance={entity.appearance}
      >
        <CommonButton
          onClick={showCardFormModal}
          disabled={isLoading || !paymentAmount}
          text={entity.text}
          border={calculatedBorder.desktop}
          mobileBorder={calculatedBorder.mobile}
          subText={entity.subText}
          padding={calculatedPadding.desktop}
          mobilePadding={calculatedPadding.mobile}
          alignSelf={entity.alignSelf}
          background={
            (entity.background ?? entity.backgroundColor) ||
            buttonBackgroundColor
          }
          mobileBackground={
            (entity.mobileBackground ?? entity.mobileBackgroundColor) ||
            buttonBackgroundColor
          }
          attrId={entity.htmlAttrId}
          width={entity.width}
          mobileWidth={entity.mobileWidth}
          textColor={entity.textColor || buttonTextColor}
          subTextColor={entity.subTextColor}
          mobileTextColor={entity.mobileTextColor || buttonTextColor}
          mobileSubTextColor={entity.mobileSubTextColor}
          textFontSize={calculatedFontSize.desktop}
          mobileTextFontSize={calculatedFontSize.mobile}
          subTextFontSize={entity.subTextFontSize}
          mobileSubTextFontSize={entity.mobileSubTextFontSize}
          textFontFamily={calculatedFontFamily.desktop}
          textFontWeight={calculatedFontWeight.desktop}
          textFontStyle={calculatedFontStyle.desktop}
          subTextFontFamily={entity.subTextFontFamily}
          subTextFontWeight={entity.subTextFontWeight}
          subTextFontStyle={entity.subTextFontStyle}
          mobileTextFontFamily={calculatedFontFamily.mobile}
          mobileTextFontWeight={calculatedFontWeight.mobile}
          mobileTextFontStyle={calculatedFontStyle.mobile}
          mobileSubTextFontFamily={entity.mobileSubTextFontFamily}
          mobileSubTextFontWeight={entity.mobileSubTextFontWeight}
          mobileSubTextFontStyle={entity.mobileSubTextFontStyle}
          boxShadow={entity.boxShadow}
          mobileBoxShadow={entity.mobileBoxShadow}
          lineHeight={calculatedLineHeight.desktop}
          mobileLineHeight={calculatedLineHeight.mobile}
          iconClassNameBefore={entity.iconClassNameBefore}
          iconClassNameAfter={entity.iconClassNameAfter}
          hover={{
            ...entity.hover,
            backgroundColor:
              entity?.hover?.backgroundColor || hoveredButtonBackgroundColor,
          }}
          mobileHover={{
            ...entity.mobileHover,
            backgroundColor:
              entity?.mobileHover?.backgroundColor ||
              entity?.hover?.backgroundColor ||
              hoveredButtonBackgroundColor,
          }}
        />
      </StyleWrapper>
      <FieldErrors errors={errors} align="center" />
    </div>
  )
}

export default memo(MercadoPagoUpsellButton)
